import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, FormControl, FormHelperText } from '@mui/material';
import FieldHelperLink from './FieldHelperLink';

/**
 * DescribedField - Wrapper para campos de formulário com descrição e helper link
 *
 * TODO 4.2: Componente wrapper que agrupa:
 *   1. Label (bold)
 *   2. Descrição (Typography body2, ≥ 1 parágrafo)
 *   3. Filhos (componentes de form)
 *   4. FieldHelperLink (opcional, se campo é derivado de entidade)
 *
 * Props:
 *   - label: string (obrigatório) - rótulo do campo
 *   - description: string (obrigatório) - descrição ≥ 1 parágrafo (O11)
 *   - helperLink?: { entity, id? } - se presente, renderiza FieldHelperLink (O12)
 *   - children: ReactNode - conteúdo do campo (TextField, Select, etc.)
 *   - error?: boolean - MUI error state
 *   - helperText?: string - MUI helper text (validação)
 *   - sx?: object - MUI sx prop para customização
 *
 * Exemplo:
 *   <DescribedField
 *     label="Foto do Profissional"
 *     description="Foto que aparece no topo da página. Em páginas profissionais,
 *       sugerimos usar a foto cadastrada em Profissionais. Para trocar a foto
 *       oficial, edite em Profissionais; para usar outra imagem só aqui, faça upload."
 *     helperLink={{ entity: 'professional' }}
 *   >
 *     <TextField ... />
 *   </DescribedField>
 *
 * Obrigações:
 *   - O10: em src/components/pages/
 *   - O11: description com ≥ 1 parágrafo
 *   - O12: usar FieldHelperLink quando campo é derivado de entidade
 */

const DescribedField = ({
  label,
  description,
  helperLink = null,
  children,
  error = false,
  helperText = null,
  sx = {},
}) => {
  return (
    <FormControl
      fullWidth
      error={error}
      sx={{
        mb: 3,
        ...sx,
      }}
    >
      {/* Label em bold */}
      <Typography
        component="label"
        variant="body1"
        sx={{
          fontWeight: 600,
          color: 'text.primary',
          display: 'block',
          mb: 1,
        }}
      >
        {label}
      </Typography>

      {/* Descrição (≥ 1 parágrafo) - O11 */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          mb: 2,
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
        }}
      >
        {description}
      </Typography>

      {/* Filhos (componentes de form) */}
      <Box sx={{ mb: helperLink ? 1.5 : 0 }}>
        {children}
      </Box>

      {/* Helper text de validação (MUI) */}
      {helperText && (
        <FormHelperText sx={{ mt: 1 }}>
          {helperText}
        </FormHelperText>
      )}

      {/* FieldHelperLink opcional - O12 */}
      {helperLink && (
        <FieldHelperLink
          entity={helperLink.entity}
          id={helperLink.id}
        />
      )}
    </FormControl>
  );
};

DescribedField.propTypes = {
  label: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  helperLink: PropTypes.shape({
    entity: PropTypes.oneOf(['professional', 'place', 'service', 'schedule']).isRequired,
    id: PropTypes.string,
  }),
  children: PropTypes.node.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  sx: PropTypes.object,
};

export default DescribedField;
